// FMO Risk Overview - Aggregate risk distribution, unaddressed risks, revenue at risk
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Shield, AlertTriangle, Users } from 'lucide-react';
import { TourBubble } from '../../components/tour/TourOverlay';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');


const FMO_RISK_TOUR_STEPS = [
  { type: 'splash', title: 'Risk Overview', description: 'Aggregate risk intelligence across every agency. See distribution by severity, unaddressed risks, risk by agency, and the top individual clients at risk of leaving.' },
  { target: 'fmo-risk-dist', emoji: '🎯', title: 'Risk Distribution', description: 'Five cards showing Critical, High, Moderate, Low, and Unaddressed risk counts. Unaddressed means at-risk clients with no recent intervention — your most urgent category.', position: 'bottom' },
  { target: 'fmo-risk-bar', emoji: '📊', title: 'Distribution Bar', description: 'Visual breakdown of risk levels across all clients. Red = critical, orange = high, yellow = moderate, green = low. A healthy network has most clients in green.', position: 'bottom' },
  { target: 'fmo-risk-byagency', emoji: '🏢', title: 'Risk by Agency', description: 'Stacked risk bars per agency. Quickly spot which agencies have disproportionate critical/high risk clients. These agencies need targeted retention support.', position: 'right' },
  { target: 'fmo-risk-topclients', emoji: '⚠️', title: 'Top Risk Clients', description: 'The 20 highest-risk clients across your entire network. Shows their agency, agent, carrier, and risk score. Escalate critical cases to agency owners directly.', position: 'left' },
  { type: 'celebration', title: 'Protect the Network!', description: 'A 1% improvement in retention across 30K clients at $150 avg commission saves $45K/year. Focus on critical and unaddressed cases first.' },
];

export default function FmoRisk() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [riskTourStep, setRiskTourStep] = useState(0);
  const [showRiskTour, setShowRiskTour] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem('fmo-risk-tour-dismissed')) setShowRiskTour(true);
  }, []);
  const advanceRiskTour = useCallback(() => { setRiskTourStep(s => { if (s + 1 >= FMO_RISK_TOUR_STEPS.length) { localStorage.setItem('fmo-risk-tour-dismissed', 'true'); setShowRiskTour(false); return 0; } return s + 1; }); }, []);
  const backRiskTour = useCallback(() => setRiskTourStep(s => Math.max(0, s - 1)), []);
  const dismissRiskTour = useCallback(() => { localStorage.setItem('fmo-risk-tour-dismissed', 'true'); setShowRiskTour(false); }, []);
  useEffect(() => {
    if (!showRiskTour) return;
    const h = (e) => { if (e.key === 'Escape') dismissRiskTour(); if (e.key === 'ArrowRight' || e.key === 'Enter') advanceRiskTour(); if (e.key === 'ArrowLeft') backRiskTour(); };
    window.addEventListener('keydown', h); return () => window.removeEventListener('keydown', h);
  }, [showRiskTour, advanceRiskTour, backRiskTour, dismissRiskTour]);

  const fetchRisk = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE}/fmo/risk`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setData(json);
    } catch (err) { console.error('[FMO Risk]', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchRisk(); }, [fetchRisk]);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><RefreshCw className="w-8 h-8 text-amber-500 animate-spin" /></div>;

  const dist = data?.distribution || {};
  const total = dist.total || 1;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/fmo/dashboard')} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#172A3A] flex items-center gap-2"><Shield className="text-red-600" /> Risk Overview</h1>
              <p className="text-sm text-gray-500">Aggregate risk across all agencies</p>
            </div>
          </div>
          <button onClick={fetchRisk} className="p-2 hover:bg-gray-100 rounded-lg"><RefreshCw size={18} className="text-gray-500" /></button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Distribution Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4" data-tour="fmo-risk-dist">
          {[
            { label: 'Critical', value: dist.critical, color: 'border-red-500', text: 'text-red-600' },
            { label: 'High', value: dist.high, color: 'border-orange-500', text: 'text-orange-600' },
            { label: 'Moderate', value: dist.moderate, color: 'border-yellow-500', text: 'text-yellow-600' },
            { label: 'Low', value: dist.low, color: 'border-green-500', text: 'text-green-600' },
            { label: 'Unaddressed', value: data?.unaddressedCount, color: 'border-purple-500', text: 'text-purple-600' },
          ].map(card => (
            <div key={card.label} className={`bg-white rounded-xl shadow-sm border-t-4 ${card.color} border border-gray-200 p-4 text-center`}>
              <p className={`text-3xl font-bold ${card.text}`}>{card.value || 0}</p>
              <p className="text-xs text-gray-500 mt-1">{card.label}</p>
            </div>
          ))}
        </div>

        {/* Distribution Bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-risk-bar">
          <h3 className="font-semibold text-gray-900 mb-4">Risk Distribution</h3>
          <div className="w-full h-8 bg-gray-100 rounded-full overflow-hidden flex">
            <div className="bg-red-500 h-full transition-all" style={{ width: `${(dist.critical / total) * 100}%` }} title={`Critical: ${dist.critical}`} />
            <div className="bg-orange-500 h-full transition-all" style={{ width: `${(dist.high / total) * 100}%` }} title={`High: ${dist.high}`} />
            <div className="bg-yellow-400 h-full transition-all" style={{ width: `${(dist.moderate / total) * 100}%` }} title={`Moderate: ${dist.moderate}`} />
            <div className="bg-green-500 h-full transition-all" style={{ width: `${(dist.low / total) * 100}%` }} title={`Low: ${dist.low}`} />
          </div>
          <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
            <span>Critical ({Math.round((dist.critical / total) * 100)}%)</span>
            <span>High ({Math.round((dist.high / total) * 100)}%)</span>
            <span>Moderate ({Math.round((dist.moderate / total) * 100)}%)</span>
            <span>Low ({Math.round((dist.low / total) * 100)}%)</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Risk by Agency */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6" data-tour="fmo-risk-byagency">
            <h3 className="font-semibold text-gray-900 mb-4">Risk by Agency</h3>
            <div className="space-y-3">
              {(data?.riskByAgency || []).map(a => (
                <div key={a.agencyId}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{a.agencyName}</span>
                    <span className="text-xs text-gray-400">{a.total} clients</span>
                  </div>
                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden flex">
                    <div className="bg-red-500 h-full" style={{ width: `${a.total > 0 ? (a.critical / a.total) * 100 : 0}%` }} />
                    <div className="bg-orange-500 h-full" style={{ width: `${a.total > 0 ? (a.high / a.total) * 100 : 0}%` }} />
                    <div className="bg-yellow-400 h-full" style={{ width: `${a.total > 0 ? (a.moderate / a.total) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Top Risk Clients */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-tour="fmo-risk-topclients">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Top Risk Clients</h3>
            </div>
            <div className="divide-y divide-gray-100 max-h-[400px] overflow-y-auto">
              {(data?.topRiskClients || []).slice(0, 20).map(c => (
                <div key={c.id} className="px-6 py-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{c.name}</p>
                    <p className="text-xs text-gray-400">{c.agencyName} &middot; {c.agentName}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">{c.carrier}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                      c.riskScore >= 75 ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                    }`}>{c.riskScore}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {showRiskTour && (
        <TourBubble stepData={FMO_RISK_TOUR_STEPS[riskTourStep]} stepIndex={riskTourStep} totalSteps={FMO_RISK_TOUR_STEPS.length}
          onNext={advanceRiskTour} onBack={backRiskTour} onDismiss={dismissRiskTour}
          splashIcon={<Shield className="w-10 h-10 text-red-600" />} maskId="fmo-risk-tour-mask" />
      )}
    </div>
  );
}
